import styles from "./toolbox-component-dropdown.module.css";
import React, { useRef, useEffect, useState } from "react";
import { TwitterPicker } from "react-color";
import { createPortal } from "react-dom";

interface ToolboxComponentDropdownProps {
  isVisible: boolean;
  onToggleVisibility: () => void;
  label: React.ReactNode;
  onMouseLeave?: () => void;
  children: React.ReactNode;
  dropUp?: boolean;
}

export const ToolboxComponentDropdown = (
  props: ToolboxComponentDropdownProps
) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [labelPosition, setLabelPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (props.isVisible && containerRef.current) {
      const offsetX = 10;
      const rect = containerRef.current.getBoundingClientRect();

      if (props.dropUp) {
        // For drop-up, position above the button
        // We'll set a negative bottom offset which we'll apply differently
        setLabelPosition({
          top: rect.top + window.scrollY, // Will be adjusted with transform
          left: rect.left + window.scrollX + offsetX,
        });
      } else {
        setLabelPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX + offsetX,
        });
      }
    }
  }, [props.isVisible, props.dropUp]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        props.isVisible &&
        containerRef.current &&
        overlayRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        !overlayRef.current.contains(event.target as Node)
      ) {
        props.onToggleVisibility();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [props, props.isVisible]);

  return (
    <>
      <div ref={containerRef} className={styles.dropDownContainer}>
        <button
          className={styles.labelButton}
          onClick={() => {
            props.onToggleVisibility();
          }}
        >
          {props.label}
        </button>
      </div>
      {props.isVisible &&
        createPortal(
          <div
            id="dropdown-portal"
            ref={overlayRef}
            className={`${styles.dropdown} ${
              props.isVisible ? styles.open : ""
            }`}
            style={{
              position: "absolute",
              top: labelPosition.top,
              left: labelPosition.left,
              zIndex: 1000,
              ...(props.dropUp && { transform: "translateY(-100%)" }),
            }}
            onMouseLeave={() => {
              if (props.onMouseLeave) {
                props.onMouseLeave();
              }
            }}
          >
            {props.children}
          </div>,
          document.body
        )}
    </>
  );
};
